import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";
import { FIELD_DEFS, FORMAT_PRESETS } from "../lib/exportFormats";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function Downloads() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailFormat, setEmailFormat] = useState("xlsx");
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [reformatTarget, setReformatTarget] = useState(null);
  const [reformatFormat, setReformatFormat] = useState("estandar");
  const [reformatCustomCols, setReformatCustomCols] = useState([]);
  const [reformatFieldValues, setReformatFieldValues] = useState({});
  const [reformatOutput, setReformatOutput] = useState("xlsx");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const token = localStorage.getItem("qre_token");

  const loadFiles = () => {
    fetch(`${API}/admin/csv-list`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (r.status === 403) { navigate("/scanner"); return null; }
        if (!r.ok) throw new Error("Error");
        return r.json();
      })
      .then(data => { if (data) setFiles(data.files); })
      .catch(() => setError(t("loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    loadFiles();
  }, []);

  const handleDownload = (filename, format) => {
    const endpoint = format === 'xlsx' 
      ? `${API}/admin/xlsx-download/${filename}`
      : `${API}/admin/csv-download/${filename}`;
    const downloadName = format === 'xlsx' ? filename.replace(".csv", ".xlsx") : filename;

    fetch(endpoint, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error("Error");
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = downloadName;
        a.click();
        URL.revokeObjectURL(a.href);
        setFiles(prev => prev.map(f => f.filename === filename ? { ...f, downloaded: true } : f));
      })
      .catch(() => alert(t("downloadErr")));
  };

  const handleShareWhatsapp = (filename) => {
    handleDownload(filename, 'xlsx');
    const message = lang === 'en'
      ? `Hi! I'm sharing the scanned codes report *${filename.replace(".csv", ".xlsx")}*. I just downloaded it, attaching it now.`
      : `¡Hola! Te comparto el reporte de códigos escaneados *${filename.replace(".csv", ".xlsx")}*. Lo acabo de descargar, lo adjunto a continuación.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleDeleteClick = (filename, downloaded) => {
    if (!downloaded) {
      alert(t("deleteNotDownloaded"));
      return;
    }
    setDeleteTarget(filename);
    setDeleteConfirmed(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmed) {
      setDeleteConfirmed(true);
      return;
    }
    fetch(`${API}/admin/csv-delete/${deleteTarget}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error("Error");
        setFiles(prev => prev.filter(f => f.filename !== deleteTarget));
        setDeleteTarget(null);
        setDeleteConfirmed(false);
      })
      .catch(() => alert(t("deleteErr")));
  };

  const handleEmailClick = (filename) => {
    setEmailTarget(filename);
    setEmailAddress("");
    setEmailFormat("xlsx");
    setEmailError("");
    setEmailSuccess("");
  };

  const handleEmailSend = () => {
    if (!emailAddress || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      setEmailError(t("invalidEmailErr"));
      return;
    }
    setEmailSending(true);
    setEmailError("");
    fetch(`${API}/admin/send-email/${emailTarget}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ to_email: emailAddress, format: emailFormat, language: lang })
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || t("sendErrDefault"));
        setFiles(prev => prev.map(f => f.filename === emailTarget ? { ...f, downloaded: true } : f));
        setEmailSuccess(t("sentTo", { email: emailAddress }));
      })
      .catch((e) => setEmailError(e.message))
      .finally(() => setEmailSending(false));
  };

  const handleReformatClick = (filename) => {
    setReformatTarget(filename);
    setReformatFormat("estandar");
    setReformatCustomCols([]);
    setReformatFieldValues({});
    setReformatOutput("xlsx");
    setPreviewData(null);
  };

  const toggleReformatCol = (key) => {
    setReformatCustomCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setPreviewData(null);
  };

  const handleReformatPreview = () => {
    setPreviewLoading(true);
    setPreviewData(null);
    fetch(`${API}/admin/preview-formatted/${reformatTarget}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({
        format: reformatFormat,
        extra_fields: reformatFieldValues,
        custom_columns: reformatCustomCols,
        output: reformatOutput
      })
    })
      .then(r => r.json())
      .then(data => setPreviewData(data))
      .catch(() => alert(t("downloadErr")))
      .finally(() => setPreviewLoading(false));
  };

  const handleReformatDownload = () => {
    fetch(`${API}/admin/export-formatted/${reformatTarget}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({
        format: reformatFormat,
        extra_fields: reformatFieldValues,
        custom_columns: reformatCustomCols,
        output: reformatOutput
      })
    })
      .then(r => {
        if (!r.ok) throw new Error("Error");
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = reformatTarget.replace(".csv", `_${reformatFormat}.${reformatOutput}`);
        a.click();
        URL.revokeObjectURL(a.href);
        setReformatTarget(null);
      })
      .catch(() => alert(t("downloadErr")));
  };

  const filtered = files.filter(f =>
    f.filename.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <h1>{t("filesTitle")}</h1>
        <div className="user-info">
          <LanguageToggle style={{ marginRight: '4px' }} />
          <button className="logout-button" onClick={() => navigate("/scanner")} data-testid="back-to-scanner">
            {t("back")}
          </button>
        </div>
      </div>

      <div style={{ padding: "16px" }}>
        <input
          type="text"
          className="login-input"
          placeholder={t("searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: "12px" }}
          data-testid="search-input"
        />

        {loading && <div className="loading-text">{t("loadingFiles")}</div>}
        {error && <div className="login-error">{error}</div>}

        {!loading && filtered.length === 0 && (
          <p style={{ textAlign: "center", color: "var(--text-light)", marginTop: "20px" }}>
            {search ? t("noResults") : t("noFiles")}
          </p>
        )}

        {filtered.map((f) => (
          <div key={f.filename} data-testid={`csv-file-${f.filename}`} style={{
            padding: "12px",
            marginBottom: "8px",
            backgroundColor: "var(--white)",
            borderRadius: "8px",
            border: `1px solid ${f.downloaded ? '#8D6E63' : 'var(--accent-color)'}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: "bold", color: "var(--primary-color)", fontSize: "13px", wordBreak: "break-all" }}>
                  {f.filename}
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-light)", marginTop: "4px" }}>
                  {f.size_kb} KB &middot; {f.modified}
                  {f.downloaded && <span style={{ color: "var(--primary-color)", fontWeight: "bold" }}> &middot; {t("downloaded")}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", flexShrink: 0, marginLeft: "8px" }}>
                <button
                  onClick={() => handleDownload(f.filename, 'xlsx')}
                  data-testid={`download-xlsx-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: "#2e7d32",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  {t("xlsx")}
                </button>
                <button
                  onClick={() => handleDownload(f.filename, 'csv')}
                  data-testid={`download-csv-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: "var(--primary-color)",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  {t("csv")}
                </button>
                <button
                  onClick={() => handleEmailClick(f.filename)}
                  data-testid={`email-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: "#1565c0",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  {t("send")}
                </button>
                <button
                  onClick={() => handleShareWhatsapp(f.filename)}
                  data-testid={`whatsapp-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: "#25D366",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  WA
                </button>
                <button
                  onClick={() => handleReformatClick(f.filename)}
                  data-testid={`reformat-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: "#8d6e63",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  {lang === 'en' ? "Format" : "Formato"}
                </button>
                <button
                  onClick={() => handleDeleteClick(f.filename, f.downloaded)}
                  data-testid={`delete-${f.filename}`}
                  style={{
                    padding: "8px 10px",
                    backgroundColor: f.downloaded ? "#c62828" : "#bbb",
                    color: "var(--white)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: f.downloaded ? "pointer" : "not-allowed",
                    fontSize: "11px",
                    fontWeight: "bold"
                  }}
                >
                  {t("deleteWord")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deleteTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }} data-testid="delete-modal">
          <div style={{
            backgroundColor: "var(--white)", borderRadius: "12px", padding: "24px",
            maxWidth: "320px", width: "90%", textAlign: "center"
          }}>
            <p style={{ color: "var(--text-color)", fontWeight: "bold", marginBottom: "8px" }}>
              {deleteConfirmed ? t("areYouSure") : t("deleteModalTitle")}
            </p>
            <p style={{ fontSize: "13px", color: "var(--text-light)", marginBottom: "16px", wordBreak: "break-all" }}>
              {deleteConfirmed
                ? t("cannotUndo")
                : deleteTarget}
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmed(false); }}
                style={{
                  padding: "10px 20px", backgroundColor: "var(--accent-color)",
                  color: "var(--text-color)", border: "none", borderRadius: "6px", cursor: "pointer"
                }}
                data-testid="delete-cancel"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: "10px 20px", backgroundColor: "#c62828",
                  color: "var(--white)", border: "none", borderRadius: "6px", cursor: "pointer"
                }}
                data-testid="delete-confirm"
              >
                {deleteConfirmed ? t("yesDelete") : t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }} data-testid="email-modal">
          <div style={{
            backgroundColor: "var(--white)", borderRadius: "12px", padding: "24px",
            maxWidth: "320px", width: "90%"
          }}>
            <p style={{ color: "var(--text-color)", fontWeight: "bold", marginBottom: "8px" }}>
              {t("sendByEmail")}
            </p>
            <p style={{ fontSize: "12px", color: "var(--text-light)", marginBottom: "12px", wordBreak: "break-all" }}>
              {emailTarget}
            </p>
            <input
              type="email"
              className="login-input"
              placeholder={t("emailDestPlaceholder")}
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              style={{ marginBottom: "12px" }}
              data-testid="email-input"
            />
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              <button
                onClick={() => setEmailFormat("xlsx")}
                data-testid="email-format-xlsx"
                style={{
                  flex: 1, padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer",
                  backgroundColor: emailFormat === "xlsx" ? "#2e7d32" : "var(--accent-color)",
                  color: emailFormat === "xlsx" ? "var(--white)" : "var(--text-color)",
                  fontWeight: "bold", fontSize: "12px"
                }}
              >
                {t("xlsx")}
              </button>
              <button
                onClick={() => setEmailFormat("csv")}
                data-testid="email-format-csv"
                style={{
                  flex: 1, padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer",
                  backgroundColor: emailFormat === "csv" ? "var(--primary-color)" : "var(--accent-color)",
                  color: emailFormat === "csv" ? "var(--white)" : "var(--text-color)",
                  fontWeight: "bold", fontSize: "12px"
                }}
              >
                {t("csv")}
              </button>
            </div>

            {emailError && <div className="login-error" data-testid="email-error">{emailError}</div>}
            {emailSuccess && (
              <p style={{ color: "#2e7d32", fontSize: "13px", fontWeight: "bold", marginBottom: "12px" }} data-testid="email-success">
                {emailSuccess}
              </p>
            )}

            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <button
                onClick={() => setEmailTarget(null)}
                data-testid="email-close"
                style={{
                  padding: "10px 20px", backgroundColor: "var(--accent-color)",
                  color: "var(--text-color)", border: "none", borderRadius: "6px", cursor: "pointer"
                }}
              >
                {emailSuccess ? t("close") : t("cancel")}
              </button>
              {!emailSuccess && (
                <button
                  onClick={handleEmailSend}
                  disabled={emailSending}
                  data-testid="email-send-confirm"
                  style={{
                    padding: "10px 20px", backgroundColor: "#1565c0",
                    color: "var(--white)", border: "none", borderRadius: "6px",
                    cursor: emailSending ? "not-allowed" : "pointer", opacity: emailSending ? 0.6 : 1
                  }}
                >
                  {emailSending ? t("sending") : t("send")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {reformatTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }} data-testid="reformat-modal">
          <div style={{
            backgroundColor: "var(--white)", borderRadius: "12px", padding: "24px",
            maxWidth: "340px", width: "90%", maxHeight: "85vh", overflowY: "auto"
          }}>
            <p style={{ color: "var(--text-color)", fontWeight: "bold", marginBottom: "8px" }}>
              {t("reformatTitle")}
            </p>
            <p style={{ fontSize: "12px", color: "var(--text-light)", marginBottom: "12px", wordBreak: "break-all" }}>
              {reformatTarget}
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "16px" }}>
              {Object.keys(FORMAT_PRESETS).map((key) => (
                <button
                  key={key}
                  onClick={() => { setReformatFormat(key); setReformatFieldValues({}); setPreviewData(null); }}
                  data-testid={`reformat-format-${key}`}
                  style={{
                    padding: "8px 12px", borderRadius: "8px", border: "2px solid var(--primary-color)", cursor: "pointer",
                    backgroundColor: reformatFormat === key ? "var(--primary-color)" : "var(--white)",
                    color: reformatFormat === key ? "var(--white)" : "var(--primary-color)",
                    fontSize: "12px", fontWeight: "bold"
                  }}
                >
                  {lang === 'en' ? FORMAT_PRESETS[key].label_en : FORMAT_PRESETS[key].label_es}
                </button>
              ))}
            </div>

            {reformatFormat === 'personalizado' && (
              <div style={{ textAlign: "left", marginBottom: "16px" }}>
                <p style={{ fontSize: "12px", color: "var(--text-light)", marginBottom: "8px" }}>{t("customColumnsLabel")}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {Object.keys(FIELD_DEFS).map((key) => (
                    <button
                      key={key}
                      onClick={() => toggleReformatCol(key)}
                      data-testid={`reformat-customcol-${key}`}
                      style={{
                        padding: "6px 10px", borderRadius: "6px", border: "1px solid var(--accent-color)", cursor: "pointer",
                        backgroundColor: reformatCustomCols.includes(key) ? "#8d6e63" : "var(--white)",
                        color: reformatCustomCols.includes(key) ? "white" : "var(--text-color)",
                        fontSize: "11px"
                      }}
                    >
                      {lang === 'en' ? FIELD_DEFS[key].label_en : FIELD_DEFS[key].label_es}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(reformatFormat === 'personalizado' ? reformatCustomCols : FORMAT_PRESETS[reformatFormat].fields).length > 0 && (
              <div style={{ textAlign: "left", marginBottom: "16px" }}>
                {(reformatFormat === 'personalizado' ? reformatCustomCols : FORMAT_PRESETS[reformatFormat].fields).map((key) => (
                  <input
                    key={key}
                    type={FIELD_DEFS[key].type === 'number' ? 'number' : 'text'}
                    maxLength={FIELD_DEFS[key].maxlen || undefined}
                    placeholder={lang === 'en' ? FIELD_DEFS[key].label_en : FIELD_DEFS[key].label_es}
                    value={reformatFieldValues[key] || ""}
                    onChange={(e) => { setReformatFieldValues(prev => ({ ...prev, [key]: e.target.value })); setPreviewData(null); }}
                    data-testid={`reformat-field-${key}`}
                    className="login-input"
                    style={{ marginBottom: "8px" }}
                  />
                ))}
              </div>
            )}

            <p style={{ fontSize: "12px", color: "var(--text-light)", marginBottom: "8px" }}>{t("outputTypeLabel")}</p>
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              <button
                onClick={() => setReformatOutput("xlsx")}
                data-testid="reformat-output-xlsx"
                style={{
                  flex: 1, padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer",
                  backgroundColor: reformatOutput === "xlsx" ? "#2e7d32" : "var(--accent-color)",
                  color: reformatOutput === "xlsx" ? "var(--white)" : "var(--text-color)",
                  fontWeight: "bold", fontSize: "12px"
                }}
              >
                {t("xlsx")}
              </button>
              <button
                onClick={() => setReformatOutput("csv")}
                data-testid="reformat-output-csv"
                style={{
                  flex: 1, padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer",
                  backgroundColor: reformatOutput === "csv" ? "var(--primary-color)" : "var(--accent-color)",
                  color: reformatOutput === "csv" ? "var(--white)" : "var(--text-color)",
                  fontWeight: "bold", fontSize: "12px"
                }}
              >
                {t("csv")}
              </button>
            </div>

            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <button
                onClick={handleReformatPreview}
                disabled={previewLoading}
                data-testid="reformat-preview-button"
                style={{
                  flex: 1, padding: "8px", borderRadius: "6px", border: "2px solid var(--primary-color)",
                  backgroundColor: "var(--white)", color: "var(--primary-color)",
                  fontWeight: "bold", fontSize: "12px", cursor: previewLoading ? "not-allowed" : "pointer"
                }}
              >
                {previewLoading ? "..." : t("previewButton")}
              </button>
            </div>

            {previewData && (
              <div data-testid="reformat-preview-table" style={{
                marginBottom: "16px", border: "1px solid var(--accent-color)", borderRadius: "8px",
                overflowX: "auto", maxWidth: "100%"
              }}>
                {previewData.rows.length === 0 ? (
                  <p style={{ fontSize: "12px", color: "var(--text-light)", padding: "12px", textAlign: "center" }}>
                    {t("previewEmpty")}
                  </p>
                ) : (
                  <>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                      <thead>
                        <tr>
                          {previewData.header.map((h, i) => (
                            <th key={i} style={{
                              padding: "6px 8px", backgroundColor: "var(--primary-color)", color: "var(--white)",
                              textAlign: "left", whiteSpace: "nowrap"
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((row, i) => (
                          <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "var(--white)" : "var(--accent-color)" }}>
                            {row.map((cell, j) => (
                              <td key={j} style={{ padding: "6px 8px", whiteSpace: "nowrap", color: "var(--text-color)" }}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ fontSize: "11px", color: "var(--text-light)", padding: "6px 8px", margin: 0 }}>
                      {t("previewRowsShown", { shown: previewData.rows.length, total: previewData.total_rows })}
                    </p>
                  </>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <button
                onClick={() => setReformatTarget(null)}
                data-testid="reformat-close"
                style={{
                  padding: "10px 20px", backgroundColor: "var(--accent-color)",
                  color: "var(--text-color)", border: "none", borderRadius: "6px", cursor: "pointer"
                }}
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleReformatDownload}
                data-testid="reformat-download-confirm"
                style={{
                  padding: "10px 20px", backgroundColor: "#8d6e63",
                  color: "var(--white)", border: "none", borderRadius: "6px", cursor: "pointer"
                }}
              >
                {lang === 'en' ? "Download" : "Descargar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Downloads;
